import { Grid, Typography } from '@mui/material';
import { useTheme } from '@mui/styles';
import { PageSelector, useAgGridPagination } from '@tassat/ag-grid';
import { ToolbarButton } from '@tassat/button';
import { AgGridFilterType, TransactionStatusCode, TransactionStatusCodeMap } from '@tassat/common-definitions';
import { DateRangeSelector } from '@tassat/formik-input';
import { TransactionHistoryTableOptions, setClientTableOptionsInitialState } from '@tassat/fred-extended-definitions';
import { Export as ExportIcon } from '@tassat/icon';
import { PoweredBySymbol } from '@tassat/symbols';
import moment from 'moment';
import React from 'react';
import ReactGA from 'react-ga4';

import TassatPayLogo from 'Images/TassatLogoAndName.svg';
import { convertDateFilterToTime } from 'Lib/agGridHelper';
import { TRANSACTIONS_PER_EXPORT } from 'Lib/constant';
import { ApplicationMuiTheme } from 'Lib/themes/muiTheme';
import Worker from 'Lib/worker/PrimusWorkerWrapper';
import { haltRealtimePledgeAction, resetPledgeDetailAction, resumeRealtimePledgeAction } from 'Reducers/actions/pledgesActions';
import { haltRealtimeTransactionsAction, resumeRealtimeTransactionsAction } from 'Reducers/actions/transactionsActions';
import { useAppDispatch, useAppSelector } from 'Reducers/store/hooks';
import { getHistoryExportAssets } from 'Selectors/assets';
import { getHaltRealtimePledges, getUser } from 'Selectors/common';
import { getTransactionsRealtimeHalted } from 'Selectors/transactions';
import { DateFilter } from 'SharedComponents/agGrid/filters/dateFilter/dateFilter';
import { TableOptionsContext, tableOptionsReducer } from 'SharedComponents/contexts/TableOptionsContext';
import { useExports } from 'SharedComponents/hooks/useExports';
import PledgeDetailsModal from 'SharedComponents/modals/pledgeDetailsModal/pledgeDetailsModal';
import TransactionDetailsModal from 'SharedComponents/modals/transactionDetailsModal';
import CollateralHistoryAgGrid from 'SharedComponents/transactions/collateralHistoryAgGrid';
import TransactionHistoryAgGrid from 'SharedComponents/transactions/transactionHistoryAgGrid';

import type { ClientTransactionHistoryProps } from '.';

export function ClientTransactionHistory(props: ClientTransactionHistoryProps) {
    const {
        classes,
        firm,
        account,
        location,
        overlayOpen
    } = props;

    const dispatch = useAppDispatch();
    const assets = useAppSelector(getHistoryExportAssets);
    const transactionsRealtimeHalted = useAppSelector(getTransactionsRealtimeHalted);
    const user = useAppSelector(getUser);
    const pledgeRealtimeHalted = useAppSelector(getHaltRealtimePledges);
    const haltRealtimeTransactions = () => dispatch(haltRealtimeTransactionsAction);
    const resumeRealtimeTransactions = () => dispatch(resumeRealtimeTransactionsAction);
    const haltRealtimePledges = () => dispatch(haltRealtimePledgeAction);
    const resumeRealtimePledges = () => dispatch(resumeRealtimePledgeAction);
    const resetPledgeDetail = () => dispatch(resetPledgeDetailAction);
    const theme = useTheme<ApplicationMuiTheme>();
    const {
        usePoweredByImage,
        useTassatPayLogoOnTransactionHistory
    } = theme?.app?.clientTheme.layoutSettings || {};
    const [ selectedTransactionId, setSelectedTransactionId ] = React.useState<string>('');
    const [ realtimeHalted, setRealtimeHalted ] = React.useState<boolean>(false);
    const [ rowCount, setRowCount ] = React.useState<number>(0);
    const [ accountHasCollateralRecords, setAccountHasCollateralRecords ] = React.useState<boolean>(false);
    const [ TableOptions, TableOptionsDispatch ] = React.useReducer(
        tableOptionsReducer,
        {
            ...setClientTableOptionsInitialState(firm, account && account.Account),
            DateCondition: `auditTime|GT|${moment()
                .subtract(1, 'day')
                .format('x')}`
        },
        (initialValue: TransactionHistoryTableOptions) => {
            if (location?.state?.tableFilters) {
                const filters = location.state.tableFilters;
                let filterCondition = '';

                if (filters.IsCompleted === true || filters.IsCompleted === false) {
                    filterCondition += `isCompleted|EQ|${filters.IsCompleted}`;
                }

                return {
                    ...setClientTableOptionsInitialState(firm, account && account.Account),
                    PageNo: 1,
                    TableFilters: [],
                    DateCondition: filters.IsCompleted ? `auditTime|GT|${convertDateFilterToTime('1m')}` : '',
                    SelectedDateFilter: filters.IsCompleted ? '1m' : 'none',
                    FilterCondition: filterCondition
                };
            }

            return initialValue;
        }
    );

    const tableContextMemo = React.useMemo(() => {
        return {
            TableOptions,
            TableOptionsDispatch
        };
    }, [ TableOptions, TableOptionsDispatch ]);
    const [setPageNum] = useAgGridPagination(TableOptionsDispatch);
    const {
        exportError,
        exportInFlight,
        setExportsContext
    } = useExports();

    const TassatLogo = React.useMemo(() => {
        if (useTassatPayLogoOnTransactionHistory) {
            return usePoweredByImage ?
                (
                    <Grid item>
                        <PoweredBySymbol
                            isAdmin={user.IsAdmin}
                            orientation='horizontal'
                        />
                    </Grid>
                )
                : (
                    <Grid item>
                        <img
                            className={classes.tassatPayLogo}
                            src={TassatPayLogo}
                        />
                    </Grid>
                );
        }

        return null;
    }, [ usePoweredByImage, useTassatPayLogoOnTransactionHistory ]);

    React.useEffect(() => {
        if (TableOptions.SelectedFilter === 'collateral') {
            TableOptionsDispatch({
                type: 'update',
                values: {
                    ...setClientTableOptionsInitialState(firm, account && account?.Account),
                    Sort: 'AuditTime|DESC'
                }
            });
        } else {
            TableOptionsDispatch({
                type: 'update',
                values: {
                    ...setClientTableOptionsInitialState(firm, account && account?.Account),
                    DateCondition: `auditTime|GT|${moment()
                        .subtract(1, 'day')
                        .format('x')}`
                }
            });
        }
    }, [
        firm,
        account
    ]);

    React.useEffect(() => {
        if (location?.state?.tableFilters) {
            const filters = location.state.tableFilters;
            let filterCondition = '';

            if (TableOptions.SelectedFilter === 'collateral') {
                TableOptionsDispatch({
                    type: 'update',
                    values: {
                        ...TableOptions,
                        PageNo: 1,
                        TableFilters: [],
                        Sort: 'AuditTime|DESC',
                        FilterCondition: filterCondition
                    }
                });
            } else {
                if (filters.IsCompleted === true || filters.IsCompleted === false) {
                    filterCondition += `isCompleted|EQ|${filters.IsCompleted}`;
                }
                TableOptionsDispatch({
                    type: 'update',
                    values: {
                        ...TableOptions,
                        PageNo: 1,
                        TableFilters: [],
                        DateCondition: filters.IsCompleted ? `auditTime|GT|${convertDateFilterToTime('1m')}` : '',
                        SelectedDateFilter: filters.IsCompleted ? '1m' : 'none',
                        FilterCondition: filterCondition
                    }
                });
            }
        }
    }, [location?.state?.tableFilters]);

    React.useEffect(() => {
        if (!transactionsRealtimeHalted && (TableOptions.FilterCondition || TableOptions.SelectedDateFilter === 'customRange')) {
            haltRealtimeTransactions();
        } else if ((transactionsRealtimeHalted && !TableOptions.FilterCondition && TableOptions.SelectedDateFilter !== 'customRange')) {
            resumeRealtimeTransactions();
        }

        if (!pledgeRealtimeHalted && (TableOptions.FilterCondition || TableOptions.SelectedDateFilter === 'customRange')) {
            haltRealtimePledges();
        } else if ((pledgeRealtimeHalted && !TableOptions.FilterCondition && TableOptions.SelectedDateFilter !== 'customRange')) {
            resumeRealtimePledges();
        }

        fetchTransactions();
    }, [
        TableOptions.DateCondition,
        TableOptions.FilterCondition,
        TableOptions.FirmCondition,
        TableOptions.WalletCondition,
        TableOptions.PageNo,
        TableOptions.Sort,
        TableOptions.SelectedDateFilter
    ]);

    React.useEffect(() => {
        if (TableOptions.SelectedFilter === 'collateral') {
            setRealtimeHalted(pledgeRealtimeHalted);
        } else {
            setRealtimeHalted(transactionsRealtimeHalted);
        }
    }, [ TableOptions.SelectedFilter, pledgeRealtimeHalted, transactionsRealtimeHalted ]);

    React.useEffect(() => {
        if (!firm.IsCollateral) {
            checkAccountHasPledges();
        }
    }, [firm.IsCollateral]);

    const fetchTransactions = async (pageNum?) => {
        const {
            PageNo,
            RecordPerPage,
            Sort,
            DateCondition,
            WalletCondition,
            FilterCondition
        } = TableOptions;

        let Condition = '';
        if (FilterCondition || DateCondition){
            if (FilterCondition && DateCondition){
                Condition = `${FilterCondition}:${DateCondition}`;
            } else if (FilterCondition && !DateCondition){
                Condition = `${FilterCondition}`;
            } else if (DateCondition && !FilterCondition) {
                Condition = `${DateCondition}`;
            }
        }

        if (TableOptions.SelectedFilter === 'none' || TableOptions.SelectedFilter === 'pending') {
            if (WalletCondition) {
                Condition = `${WalletCondition}${Condition && `:${Condition}`}`;
            }
            const res = await Worker.getInstance()
                .getTransactions({
                    Condition: Condition,
                    Sort,
                    PageNo: pageNum || PageNo,
                    RecordPerPage
                }) || { data: undefined };
            setRowCount(res.data && res.data.TotalCount);

            return res;
        }

        if (TableOptions.SelectedFilter === 'collateral') {
            if (WalletCondition) {
                Condition = `PledgingAccountAddress|EQ|${account?.Account}^ReceivingAccountAddress|EQ|${account?.Account}${Condition && `:${Condition}`}`;
            }

            const res = await Worker.getInstance()
                .getCollateral({
                    Condition: Condition,
                    Sort: 'AuditTime|DESC',
                    PageNo: pageNum || PageNo,
                    RecordPerPage
                }) || { data: undefined };
            setRowCount(res.data && res.data.TotalCount);

            return res;
        }
    };

    const fetchTransactionsExport = async () => {
        const {
            Sort,
            DateCondition,
            WalletCondition,
            FilterCondition
        } = TableOptions;

        let Condition = '';
        if (FilterCondition || DateCondition){
            if (FilterCondition && DateCondition){
                Condition = `${FilterCondition}:${DateCondition}`;
            } else if (FilterCondition && !DateCondition){
                Condition = `${FilterCondition}`;
            } else if (DateCondition && !FilterCondition) {
                Condition = `${DateCondition}`;
            }
        }

        if (TableOptions.SelectedFilter !== 'collateral') {
            ReactGA.event('click', {
                event_category: 'engagement',
                event_label: 'transaction_export'
            });

            if (WalletCondition){
                Condition = `${WalletCondition}${Condition && `:${Condition}`}`;
            }

            let query = `&Condition=${Condition}`;
            query += `&Sort=${Sort}&RecordPerPage=${TRANSACTIONS_PER_EXPORT}`;

            setExportsContext((exportsContext) => ({
                ...exportsContext,
                totalChunks: Math.floor(rowCount / TRANSACTIONS_PER_EXPORT) + 1,
                chunkCounter: 0,
                exportInFlight: true,
                RecordType: 'client-transaction',
                filterQuery: query,
                opts: { assets: assets as any }
            }));
        } else {
            ReactGA.event('click', {
                event_category: 'engagement',
                event_label: 'collateral_export'
            });

            if (WalletCondition) {
                Condition = `PledgingAccountAddress|EQ|${account?.Account}^ReceivingAccountAddress|EQ|${account?.Account}${Condition && `:${Condition}`}`;
            }

            let query = `&Condition=${Condition}`;
            query += `&Sort=AuditTime|DESC&RecordPerPage=${TRANSACTIONS_PER_EXPORT}`;

            setExportsContext((exportsContext) => ({
                ...exportsContext,
                totalChunks: Math.floor(rowCount / TRANSACTIONS_PER_EXPORT) + 1,
                chunkCounter: 0,
                exportInFlight: true,
                RecordType: 'client-collateral',
                filterQuery: query,
                opts: { assets: assets as any }
            }));
        }
    };

    const selectTimeRange = (startDate: string | moment.Moment, endDate: string | moment.Moment) => {
        ReactGA.event('click', {
            event_category: 'engagement',
            event_label: 'transaction_date_filter'
        });
        if (moment.isMoment(startDate) && moment.isMoment(endDate)) {
            TableOptionsDispatch({
                type: 'update',
                values: {
                    ...TableOptions,
                    SelectedDateFilter: 'customRange',
                    DateCondition: `auditTime|GT|${startDate.valueOf()}:auditTime|LT|${endDate.valueOf()}`,
                    PageNo: 1,
                    TableFilters: [],
                    FilterCondition: ''
                }
            });
        } else if (typeof startDate === 'string') {
            TableOptionsDispatch({
                type: 'update',
                values: {
                    ...TableOptions,
                    SelectedDateFilter: startDate,
                    DateCondition: `auditTime|GT|${convertDateFilterToTime(startDate)}`,
                    PageNo: 1,
                    TableFilters: [],
                    FilterCondition: ''
                }
            });
        }
    };

    const selectFilter = (filterType: string) => {
        ReactGA.event('click', {
            event_category: 'engagement',
            event_label: filterType === 'pending' ? 'transaction_filter_pending' : filterType === 'collateral' ? 'transaction_filter_collateral' : 'transaction_filter_all'
        });
        const optionsUpdate = {
            type: 'update',
            values: {}
        };
        if (filterType === 'none') {
            optionsUpdate.values = {
                TableFilters: [],
                FilterCondition: '',
                SelectedFilter: 'none',
                SelectedDateFilter: '1d',
                DateCondition: `auditTime|GT|${moment()
                    .subtract(1, 'day')
                    .format('x')}`,
                Sort: 'auditTime|DESC'
            };
        } else if (filterType === 'pending') {
            optionsUpdate.values = {
                TableFilters: [
                    {
                        column: 'Status',
                        operation: AgGridFilterType.Equal,
                        value: 'V'
                    }
                ],
                FilterCondition: `Status|EQ|${TransactionStatusCodeMap[TransactionStatusCode.PENDING_APPROVAL]?.value}`,
                SelectedFilter: 'pending',
                SelectedDateFilter: '',
                DateCondition: '',
                Sort: 'auditTime|DESC'
            };
        } else if (filterType === 'collateral') {
            optionsUpdate.values = {
                TableFilters: [],
                FilterCondition: '',
                SelectedFilter: 'collateral',
                SelectedDateFilter: '',
                DateCondition: '',
                Sort: 'AuditTime|DESC'
            };
        }
        TableOptionsDispatch(optionsUpdate);
    };

    const clearFilter = () => {
        TableOptionsDispatch({
            type: 'update',
            values: {
                ...setClientTableOptionsInitialState(firm, account && account?.Account),
                DateCondition: `auditTime|GT|${moment()
                    .subtract(1, 'day')
                    .format('x')}`
            }
        });
    };

    const checkAccountHasPledges = async () => {
        try {
            const res = await Worker.getInstance()
                .getAccountHasCollateralRecords({ Condition: account ? `PledgingAccountAddress|IN|${account?.Account}^ReceivingAccountAddress|IN|${account?.Account}` : '' }) || { data: undefined };
            setAccountHasCollateralRecords(res.data && res.data.TotalCount > 0);
        } catch (err) {
            setAccountHasCollateralRecords(false);
        }
    };
    if (!firm || (firm && Object.keys(firm).length === 0)) return <div />;

    return (
        <TableOptionsContext.Provider value={tableContextMemo}>
            <Grid
                className={classes.root}
                container
                direction='column'
                justifyContent='flex-start'
                spacing={0}
                wrap='nowrap'
            >
                <Grid
                    className={classes.textFilterContainer}
                    container
                    direction='row'
                    justifyContent='space-between'
                    spacing={0}
                    wrap='nowrap'
                >
                    <Grid
                        alignItems='flex-end'
                        container
                        direction='row'
                        item
                        wrap='nowrap'
                        xs={6}
                    >
                        <Typography
                            className={`${TableOptions.SelectedFilter === 'none' && classes.selectedTransactionFilter} ${classes.transactionFilter}`}
                            onClick={() => selectFilter('none')}
                            variant='h4'
                        >
                            All {account && 'Account'} Transactions
                        </Typography>
                        {(!account || (account && account.AccessControl)) &&
                            <Typography
                                className={`${TableOptions.SelectedFilter === 'pending' && classes.selectedTransactionFilter} ${classes.transactionFilter}`}
                                onClick={() => selectFilter('pending')}
                                variant='h4'
                            >
                                Pending Approval
                            </Typography>}
                        {(firm.IsCollateral || accountHasCollateralRecords) &&
                            <Typography
                                className={`${TableOptions.SelectedFilter === 'collateral' && classes.selectedTransactionFilter} ${classes.transactionFilter}`}
                                onClick={() => selectFilter('collateral')}
                                variant='h4'
                            >
                                Collateral Management
                            </Typography>}
                    </Grid>
                    {TassatLogo}
                </Grid>
                <Grid
                    className={classes.dateFilterContainer}
                    container
                    direction='row'
                    justifyContent='space-between'
                    spacing={0}
                    wrap='nowrap'
                >
                    <Grid
                        alignItems='center'
                        className={classes.filtersContainer}
                        container
                        direction='row'
                        item
                        spacing={0}
                        wrap='nowrap'
                    >
                        <DateFilter
                            disabled={TableOptions.SelectedFilter === 'pending' || TableOptions.SelectedFilter === 'collateral'}
                            selectTimeRange={selectTimeRange}
                            timeFrameinDays={TableOptions.SelectedDateFilter}
                        />
                        <DateRangeSelector
                            className={classes.dateRangeSelectorContainer}
                            disabled={TableOptions.SelectedFilter === 'pending' || TableOptions.SelectedFilter === 'collateral'}
                            maxDate={moment()}
                            selectTimeRange={selectTimeRange}
                            timeFrameInDays={TableOptions.SelectedDateFilter}
                        />
                        <Typography
                            className={classes.filterOption}
                            onClick={clearFilter}
                            variant='h5'
                        >
                            Clear Filters
                        </Typography>
                        <Typography
                            className={classes.realtimeTag}
                            variant='h5'
                        >
                            Real Time Updates
                            <span className={`${classes.realtimeState} ${realtimeHalted ? classes.realtimeDisabled : classes.realtimeEnabled}`}>{realtimeHalted ? 'OFF' : 'ON'}</span>
                        </Typography>
                    </Grid>
                    <Grid
                        alignItems='center'
                        className={classes.filtersContainer}
                        container
                        direction='row'
                        item
                        spacing={0}
                        wrap='nowrap'
                    >
                        <PageSelector
                            changePageOnApplyOnly
                            currentPageNum={TableOptions.PageNo}
                            onPageChange={setPageNum}
                            totalRows={rowCount}
                        />
                        <ToolbarButton
                            actionInProgress={exportInFlight}
                            className={classes.exportButton}
                            Icon={ExportIcon}
                            iconClassName={classes.exportIcon}
                            onClick={fetchTransactionsExport}
                            text={exportError}
                        />
                    </Grid>
                </Grid>
                <Grid
                    className={classes.content}
                    container
                    direction='row'
                    item
                    justifyContent='flex-start'
                    spacing={0}
                    xs={12}
                >
                    {TableOptions.SelectedFilter === 'collateral' ?
                        (
                            <CollateralHistoryAgGrid
                                overlayOpen={overlayOpen}
                                setSelectedPledgeId={(id) => {
                                    ReactGA.event('click', {
                                        event_category: 'engagement',
                                        event_label: 'collateral_row_selected'
                                    });
                                    setSelectedTransactionId(id);
                                }}
                                tableOptions={TableOptions}
                            />
                        )
                        : (
                            <TransactionHistoryAgGrid
                                overlayOpen={overlayOpen}
                                setSelectedTransactionId={(id) => {
                                    ReactGA.event('click', {
                                        event_category: 'engagement',
                                        event_label: 'transaction_row_selected'
                                    });
                                    setSelectedTransactionId(id);
                                }}
                                tableOptions={TableOptions}
                            />
                        )}
                </Grid>
                {TableOptions.SelectedFilter === 'collateral' ?
                    (
                        <PledgeDetailsModal
                            accountId={account?.AccountId}
                            onClose={() => {
                                setSelectedTransactionId('');
                                resetPledgeDetail();
                            }}
                            open={Boolean(selectedTransactionId)}
                            selectedPledgeId={selectedTransactionId}
                        />
                    )
                    : (
                        <TransactionDetailsModal
                            onClose={() => setSelectedTransactionId('')}
                            open={Boolean(selectedTransactionId)}
                            selectedTransactionId={selectedTransactionId}
                        />
                    )}
            </Grid>
        </TableOptionsContext.Provider>
    );
}